import { useEffect, useState } from "react";
import { Link } from "react-router";
import { useApi } from "../../../app/ApiProvider";
import { useAuth, useScope } from "../../../app/AuthProvider";
import { useAction, useAsync } from "../../../app/useAsync";
import { Badge, Button, Callout, Card, cx, PageHeader, TextArea } from "../../../ui/primitives";
import { ErrorState, LoadingState } from "../../../ui/states";
import { BOARD_COLUMNS, Conversation, OrderCard, summarizeLatestBurnIn } from "./components";

export function BoardPage() {
	const { client, pin } = useApi();
	const { session } = useAuth();
	const scope = useScope();
	const [message, setMessage] = useState("");
	const board = useAsync(
		() => client.getWorkOrderBoard(scope),
		[client, scope.tenantId, scope.projectId],
	);
	const send = useAction(async () => client.sendBoardDesignMessage(scope, message));
	const boardData = board.status === "ready" ? board.data : null;
	const burnIn = boardData ? summarizeLatestBurnIn(boardData.orders, boardData.progress) : null;

	useEffect(() => {
		if (client.mode !== "real" || !boardData) return;
		const liveOrders = boardData.orders.filter((order) => boardData.progress[order.id]?.live);
		const subscriptions = liveOrders.map((order) =>
			client.subscribeWorkOrderProgress(scope, order.id, {
				lastSequence: boardData.progress[order.id]?.sequence,
				onEvent: board.reload,
				onGap: board.reload,
				onError: () => undefined,
			}),
		);
		if (boardData.designSessionId) {
			const lastSequence = Math.max(-1, ...boardData.designFrames.map((frame) => frame.sequence));
			subscriptions.push(
				client.subscribeDesignFrames(scope, boardData.designSessionId, {
					lastSequence,
					onEvent: board.reload,
					onGap: board.reload,
					onError: () => undefined,
				}),
			);
		}
		return () => {
			for (const subscription of subscriptions) subscription.close();
		};
	}, [client, scope, boardData, board.reload]);

	async function submit(event: React.FormEvent) {
		event.preventDefault();
		if (!message.trim()) return;
		const result = await send.run();
		if (!result) return;
		setMessage("");
		board.reload();
	}

	return (
		<>
			<PageHeader
				eyebrow={session?.tenant.name}
				title="発注ボード"
				description="マネージャーが依頼を受け、関連 Agent に依頼 → 報告 → 発注者へ確認／納品、の流れで進捗を見ます。"
				actions={
					<Badge tone="brand">
						Contract {pin.contractVersion} / {client.mode === "real" ? "Real" : "Mock"}
					</Badge>
				}
			/>

			{board.status === "loading" && <LoadingState label="ボードを読み込んでいます" />}
			{board.status === "error" && <ErrorState error={board.error} onRetry={board.reload} />}

			{board.status === "ready" && (
				<div className="space-y-5">
					{burnIn && (
						<Card
							title={`Burn-in ${burnIn.accepted}/${burnIn.size}`}
							description={`${burnIn.campaignId}・${burnIn.detail}`}
							action={
								<Badge
									tone={
										burnIn.status === "passed"
											? "success"
											: burnIn.status === "failed"
												? "danger"
												: "warning"
									}
								>
									{burnIn.status === "passed"
										? "合格"
										: burnIn.status === "failed"
											? "やり直し"
											: "進行中"}
								</Badge>
							}
						/>
					)}
					<Card
						title="ボードに相談する"
						description="Project の design セッションとして会話し、自然文から依頼の下書きを作ります。"
						action={
							<Badge tone={client.mode === "real" ? "success" : "neutral"}>
								{client.mode === "real" ? "Real API / SSE" : "Mock"}
							</Badge>
						}
					>
						<div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(18rem,0.55fr)]">
							<Conversation frames={board.data.designFrames} />
							<form onSubmit={submit} className="space-y-2">
								<label htmlFor="board-message" className="ag-label">
									依頼したいこと
								</label>
								<TextArea
									id="board-message"
									rows={4}
									value={message}
									onChange={(event) => setMessage(event.target.value)}
									placeholder="例: ボードの空状態を分かりやすくして、モバイルでも確認できるようにしたい"
								/>
								<div className="flex items-center justify-between gap-3">
									<p className="text-[11.5px] text-ink-500">
										送信すると下書きカードを1件起票します。
									</p>
									<Button type="submit" size="sm" disabled={send.pending || !message.trim()}>
										{send.pending ? "起票中…" : "相談して起票"}
									</Button>
								</div>
								{Boolean(send.error) && (
									<Callout tone="danger">起票できませんでした。もう一度お試しください。</Callout>
								)}
							</form>
						</div>
					</Card>

					<section aria-labelledby="board-columns-title">
						<div className="mb-3 flex items-center justify-between gap-3">
							<h2 id="board-columns-title" className="text-[16px] font-bold text-ink-900">
								進行状況
							</h2>
							<p className="text-[12px] text-ink-500">横にスクロールして全工程を確認できます</p>
						</div>
						<div className="grid auto-cols-[minmax(16rem,1fr)] grid-flow-col gap-3 overflow-x-auto pb-3">
							{BOARD_COLUMNS.map((column) => {
								const orders = board.data.orders.filter(
									(order) => !order.deletedAt && order.status === column.status,
								);
								return (
									<section
										key={column.status}
										className={cx("min-h-60 rounded-2xl border p-3", column.tone)}
										aria-label={column.label}
									>
										<header className="mb-3 flex items-center justify-between gap-2">
											<h3 className="text-[13px] font-bold text-ink-800">{column.label}</h3>
											<Badge tone={column.status === "blocked" ? "danger" : "neutral"}>
												{orders.length}
											</Badge>
										</header>
										<div className="space-y-2.5">
											{orders.map((order) => (
												<OrderCard
													key={order.id}
													order={order}
													progress={board.data.progress[order.id]}
												/>
											))}
											{orders.length === 0 && (
												<p className="rounded-xl border border-dashed border-ink-200 px-3 py-5 text-center text-[12px] text-ink-400">
													カードはありません
												</p>
											)}
										</div>
									</section>
								);
							})}
						</div>
					</section>

					{board.data.orders.some((order) => order.deletedAt) && (
						<Callout title="削除済みカード" tone="neutral">
							<div className="mt-1 flex flex-wrap gap-3">
								{board.data.orders
									.filter((order) => order.deletedAt)
									.map((order) => (
										<Link
											key={order.id}
											to={`/app/orders/${order.id}`}
											className="font-semibold text-brand-700 hover:underline"
										>
											{order.title}（復元する）
										</Link>
									))}
							</div>
						</Callout>
					)}
				</div>
			)}
		</>
	);
}
